// Single source of truth for "where is the team right now?".
// Priority: manual "Currently here" > today within a country's dates >
// Home/departure (before trip starts or after it ends) > trip name.
const clean = (n) => (n || "").replace(/[^\p{L}\s,]/gu, "").split(",").pop().trim();
const dayStart = (s) => { const x = new Date(s); x.setHours(0, 0, 0, 0); return x; };

// Chronological ordering: by arrival date first, then manual drag order for
// stops without dates. Single source of truth for list/map/badges.
export function orderCountries(countries = []) {
  return [...countries].sort((a, b) => {
    const ad = a.arrive_date || "", bd = b.arrive_date || "";
    if (ad && bd) return ad.localeCompare(bd) || ((a.order ?? 999) - (b.order ?? 999));
    if (ad && !bd) return -1;
    if (!ad && bd) return 1;
    return (a.order ?? 999) - (b.order ?? 999);
  });
}

export function getTripStatus(config, countries = [], origin = null) {
  const countryLabel = (c) => [c.city, clean(c.name)].filter(Boolean).join(", ");
  const homeLabel = origin && (origin.country || origin.city)
    ? [origin.city, clean(origin.country)].filter(Boolean).join(", ")
    : null;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = config?.start_date ? dayStart(config.start_date) : null;
  const end = config?.end_date ? dayStart(config.end_date) : null;
  const sorted = orderCountries(countries);

  let phase = "preparing";
  if (start && today > (end || start) && end && today > end) phase = "complete";
  else if (start && today >= start) phase = (end && today > end) ? "complete" : "on_road";
  else phase = "preparing";

  const manual = sorted.find((c) => c.is_current);
  const within = sorted.find((c) => c.arrive_date && c.depart_date && dayStart(c.arrive_date) <= today && today <= dayStart(c.depart_date));
  const arrived = [...sorted].reverse().find((c) => c.arrive_date && dayStart(c.arrive_date) <= today && (!c.depart_date || today <= dayStart(c.depart_date)));

  let current = null;
  if (manual) current = manual;
  else if (phase === "on_road") current = within || arrived || null;

  let label, atHome = false, kind = "trip";
  if (current) { label = countryLabel(current); kind = "country"; }
  else { label = homeLabel || config?.trip_name || ""; atHome = !!homeLabel; kind = "home"; }

  const phaseLabel = phase === "on_road" ? "On the road" : phase === "complete" ? "Trip complete" : "Preparing";
  return { phase, phaseLabel, current, currentId: current?.id || null, label, atHome, kind };
}

// Per-country phase + progress for the country cards.
export function getCountryProgress(c, currentId) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const a = c.arrive_date ? dayStart(c.arrive_date) : null;
  const d = c.depart_date ? dayStart(c.depart_date) : null;
  const manualCurrent = currentId ? c.id === currentId : !!c.is_current;
  let phase = "upcoming";
  if (a && d) {
    if (today > d) phase = "past";
    else if (today >= a) phase = "current";
    else phase = "upcoming";
  }
  if (manualCurrent && phase !== "past") phase = "current";
  let daysTotal = null, daysDone = null, pct = 0, weekTotal = null, weekNow = null;
  if (a && d) {
    daysTotal = Math.round((d - a) / 86400000) + 1;
    if (phase === "past") { daysDone = daysTotal; pct = 100; }
    else if (phase === "current") {
      daysDone = Math.min(daysTotal, Math.max(1, Math.round((today - a) / 86400000) + 1));
      pct = Math.round((daysDone / daysTotal) * 100);
    } else { daysDone = 0; pct = 0; }
    weekTotal = Math.ceil(daysTotal / 7);
    weekNow = daysDone ? Math.ceil(daysDone / 7) : 0;
  }
  return { phase, daysTotal, daysDone, pct, weekTotal, weekNow, manualCurrent };
}
